import React, {useCallback, useEffect, useState} from 'react';
import {
  Form, Input, Modal, Switch, Radio, Select, Table, Icon, Alert, Skeleton,
} from 'antd';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  selectEditState,
  selectUsersRequest,
  selectCreateUserRequest,
  selectLocations,
} from '../../../../state/ui/configuration/users/userUiReducer';
import { selectUser } from '../../../../state/domain/users/userReducer';
import { cancelEditUser, requestAddUser, requestUpdateUser } from '../../../../state/ui/configuration/users/userUiActions';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import {getStatus, userStatusNames, roleNames, userIsAdmin, userIsCompanyAdmin} from './user-util';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import {filterSelectField, locationShape, sortAlphabeticallyByAttribute} from "../inspection/config-util";
import {requestLocationForSelect} from "../../../../state/ui/configuration/location/locationUiActions";
import './EditUser.css';
import {userIsAdminConsole} from "../../../../utility/security/security-util";

const { Column } = Table;


// FIELDS
/**
 * The field for the description label.  Allows clear, and max length set.  Required plus extra duplicate validation.
 * The rules are injected into the form using the form field decorator.
 */
const UsernameField = injectIntl(({
  form, user, intl, checkForDuplicates,
}) => (
  <Form.Item required label={<FormattedMessage id="email" />}>
    { form.getFieldDecorator('username', {
      initialValue: user ? user.username : undefined,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmail' }) },
        { type: 'email', message: intl.formatMessage({ id: 'invalidEmail' }) },
        {
          validator(rule, value, callback) {
            if (value !== (user ? user.username : '')
              && checkForDuplicates(user ? user.userId : null, value)) {
              callback(intl.formatMessage({ id: 'Duplicate' }));
            }
            callback();
          },
        },
      ],
    })(
      <Input
        type="text"
        maxLength={80}
        disabled={!!user && getStatus(user).status !== userStatusNames.INVITED}
        allowClear={!user}
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

/**
 * The field for the display name label.  Allows clear, and max length set.
 * The rules are injected into the form using the form field decorator.
 */
const DisplayNameField = injectIntl(({
  form, user, intl,
}) => (
  <Form.Item required label={<FormattedMessage id="displayName" />}>
    { form.getFieldDecorator('displayName', {
      initialValue: user ? user.displayName : undefined,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationDisplayName' }) },
      ],
    })(
      <Input
        type="text"
        maxLength={250}
        allowClear
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

const LocationField = injectIntl(({
                                     form, intl, locations, loading = false, addLocationAction, selectValue
                                   }) => (
        <div className="add-location-div">
          <span className="add-location-label">
            <Icon type="plus" />&nbsp;
            <FormattedMessage id="addLocation" />
          </span>
          <Select
              mode="default"
              showArrow
              placeholder={<FormattedMessage id="select" />}
              loading={loading}
              filterOption={filterSelectField}
              value={selectValue}
              onChange={(value, option) => {
                addLocationAction(value != null ? value : null);
              }}
          >
            {
              locations.map(loc => (
                  <Select.Option key={loc.locationCode} value={loc.locationCode}>{loc.locationDescription}</Select.Option>
              ))
            }
          </Select>
        </div>
));

const LocationsMiniTable = injectIntl(({
                        form, locationList, currentLocCode, setCurrentLocCode, intl, loading = false, onLocationRemove
                      }) => (
    <Form.Item required label={<FormattedMessage id="locations" />} style={{  paddingBottom: '0px', }}>
      <Table
          pagination={false}
          scroll={{ x: false, y: false }}
          loading={loading}
          dataSource={locationList != null ? locationList : []}
          rowKey={loc => loc.locationDescription}
          locale={{ emptyText: <FormattedMessage id="locationTableEmpty" /> }}
      >
        <Column
            title={<FormattedMessage id="location" />}
            dataIndex="locationDescription"
            key="locationDescription"
            align="left"
        />
        <Column
            title={<FormattedMessage id="current" />}
            align="center"
            render={(text, record) => (
                <Radio checked={record.locationCode === currentLocCode} onClick={() => {
                  setCurrentLocCode(record.locationCode);
                }} />
            )}
        />
        <Column
            align="center"
            width="50px"
            render={(text, record) => (
                <Icon type="delete" theme="filled" onClick={() => onLocationRemove(record.locationCode)} />
            )}
        />
      </Table>
    </Form.Item>
));

const RoleSwitch = injectIntl(({
  role, name, form, bynxAdmin, onOwn, clearRole, setIsInspector,
}) => (
    <tr>
      <td>
        <label htmlFor={role} >
          <FormattedMessage id={name} />
        </label>
      </td>
      <td>&nbsp;</td>
      <td>
        <Switch
            id={role}
            checked={bynxAdmin || !!form.getFieldValue('linkedRoles').find(item => item === role)}
            disabled={bynxAdmin}
            onChange={(checked) => {
              const newRoles = form.getFieldValue('linkedRoles');
              if (checked) {
                if (onOwn) {
                  newRoles.splice(0, newRoles.length);
                }
                newRoles.push(role);
                if (clearRole) {
                  const indexClear = newRoles.indexOf(clearRole);
                  if (indexClear > -1) {
                    newRoles.splice(indexClear, 1);
                  }
                }
              } else {
                const roleIndex = form.getFieldValue('linkedRoles').indexOf(role);
                newRoles.splice(roleIndex, 1);
              }
              let newVar = newRoles ? !!newRoles.find(role => role === roleNames.INSPECTOR) : false;
              setIsInspector(newVar);
              form.setFieldsValue({ linkedRoles: newRoles });
              form.validateFields();
            }}
        />
      </td>
    </tr>
));

const RolesFields = injectIntl(({
  form, user, isLastAdmin, isLastCompanyAdmin, intl, setIsInspector, sessionUser,
}) => (
  <Form.Item required={userIsAdminConsole(sessionUser)} label={<FormattedMessage id="roles" />}>
    {
      form.getFieldDecorator('linkedRoles', {
        // Default to inspector when creating a new user
        initialValue: user ? user.linkedRoles.slice() : (userIsAdminConsole(sessionUser) ? [] : (!userIsAdmin(sessionUser) ? [roleNames.READ_ONLY] : [roleNames.INSPECTOR])),
        rules: [
          {
            validator(rule, value, callback) {
              if (value == null) { value = []; }
              // admin console users must have a role
              if (userIsAdminConsole(sessionUser) && value.length === 0) {
                callback(intl.formatMessage({ id: 'roleRequired' }));
              }
              // doesn't show the message if no roles are selected to prevent double messages showing.
              if (userIsAdmin(sessionUser) && isLastAdmin && !value.find(role => role === roleNames.ADMIN) && value.length !== 0) {
                callback(intl.formatMessage({ id: 'adminRequired' }));
              }
              if (userIsAdminConsole(sessionUser) && isLastCompanyAdmin && !value.find(role => role === roleNames.COMPANY_ADMIN) && value.length !== 0) {
                callback(intl.formatMessage({ id: 'adminRequiredCo' }));
              }
              callback();
            },
          },
        ],
      })
      (!userIsAdminConsole(sessionUser) ?
      (
        <table>
          <tbody>
            <RoleSwitch role={roleNames.ADMIN} name="administrator" form={form} setIsInspector={setIsInspector} />
            <RoleSwitch role={roleNames.INSPECTOR} name="inspector" form={form} setIsInspector={setIsInspector} />
          </tbody>
        </table>
      ) :
      ( userIsCompanyAdmin(sessionUser) ?
        (
            <table>
              <tbody>
                <RoleSwitch role={roleNames.COMPANY_ADMIN} name="companyAdministrator" form={form} setIsInspector={setIsInspector} />
                <RoleSwitch role={roleNames.COMPANY_FINANCE} name="companyFinance" form={form} setIsInspector={setIsInspector} />
              </tbody>
            </table>
        ) :
        (
            <table>
              <tbody>
                <RoleSwitch role={roleNames.CONSOLE_ADMIN} name="systemAdministrator" form={form} clearRole={roleNames.COMPANY_ADMIN} bynxAdmin={user != null && user.username === 'bynx.apps@bynx.com'} setIsInspector={setIsInspector} />
                <RoleSwitch role={roleNames.CONSOLE_FINANCE} name="financeAdministrator" form={form} clearRole={roleNames.COMPANY_ADMIN} setIsInspector={setIsInspector} />
                <RoleSwitch role={roleNames.COMPANY_ADMIN} name="companyAdministrator" form={form} onOwn={true} setIsInspector={setIsInspector} />
              </tbody>
            </table>
        )
      ))
    }
  </Form.Item>
));

const StatusField = injectIntl(({
  form, user, intl, sessionUsername,
}) => (
  <Form.Item required label={<FormattedMessage id="status" />}>
    { form.getFieldDecorator('status', {
      initialValue: user ? getStatus(user).status : userStatusNames.INVITED,
      rules: [
        {
          validator(rule, value, callback) {
            // Cannot disable self
            if (user && user.username === sessionUsername && value === userStatusNames.DISABLED) {
              callback(intl.formatMessage({ id: 'disableNotAllowed' }));
            }
            callback();
          },
        },
      ],
    })(
      <Radio.Group buttonStyle="solid" onChange={(value) => { form.setFieldsValue({ status: value }); form.validateFields(); }}>
        <Radio.Button disabled={!user || getStatus(user).status === userStatusNames.INVITED} value="active"><FormattedMessage id="active" /></Radio.Button>
        <Radio.Button disabled={!!user && getStatus(user).status !== userStatusNames.INVITED} value="invited"><FormattedMessage id="invited" /></Radio.Button>
        <Radio.Button disabled={!user || getStatus(user).status === userStatusNames.INVITED} value="disabled"><FormattedMessage id="inactive" /></Radio.Button>
      </Radio.Group>,
    )}
  </Form.Item>
));

// MODAL AND FORM
const EditUser = injectIntl(Form.create({ name: 'edit-user' })(({
  intl, form, visible, setVisible, user, locationObjects, onCreate, onUpdate, usernameValidator, isLastAdmin, sessionUser,
  onCancel, isLoading, isSubmitting, validationBox, getLocations, locations, isLastCompanyAdmin, ...rest
}) => {
  // Current and set of locations
  const [currentLocationCode, setCurrentLocationCode] = useState(null);
  const [availLocations, setAvailLocations] = useState([]);
  const [selectValue, setSelectValue] = useState(null);
  const [validationLocationsBox, setValidationLocationsBox] = useState(null);

  // Location functions
  const adjustLocations = useCallback(() => {
    const newLocations = [];
    const keepLocations = [];
    locations.forEach(loc => newLocations.push(loc));  //reset from get locations
    if (newLocations.length > 0 && locationObjects != null) {
      newLocations.forEach(location => {
        if (locationObjects.findIndex(userLoc => userLoc.locationCode === location.locationCode) < 0) {
          keepLocations.push(location);
        }
      })
    }
    return keepLocations;
  }, [locations, locationObjects]);

  const addLocationAction = (locCode) => {
    locations.forEach(location => {
      if (location.locationCode === locCode) {
        locationObjects.push(location);
        if (locationObjects.length === 1){
          setCurrentLocationCode(locCode);
        }
      }
    })
    setSelectValue(null);
    setAvailLocations(adjustLocations());
  }

  const onLocationRemove = (locCode) => {
    let index = 0;
    let deleteIndex = -1;
    let changeCurrent = false;

    locationObjects.forEach(location => {
      if (location.locationCode === locCode) {
        deleteIndex = deleteIndex === -1 ? index : deleteIndex;
        if (location.locationCode === currentLocationCode){
          changeCurrent = true;
        }
      }
      index++;
    })
    if (deleteIndex > -1) {
      locationObjects.splice(deleteIndex, 1);
    }
    setAvailLocations(adjustLocations());
    if (changeCurrent || locationObjects.length < 1) {
      setCurrentLocationCode(locationObjects.length < 1 ? -1 : locationObjects[0].locationCode);
    }
  }

  // Load locations for select
  useEffect(() => {
    getLocations();
  }, [getLocations]);

  const isCreating = !user;
  const titleId = isCreating ? 'inviteNewUser' : 'editUser';
  const buttonId = isCreating ? 'sendInvite' : 'save';

  const { resetFields } = form;

  const isAdmin = user ? !!user.linkedRoles.find(role => role === roleNames.ADMIN) : false;

  const [isInspector, setIsInspector] = useState(false);

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) {
      resetFields();
      setIsInspector(user ? !!user.linkedRoles.find(role => role === roleNames.INSPECTOR) : !(userIsAdminConsole(sessionUser)));
    }
  }, [user, resetFields, visible, sessionUser, setIsInspector]);

  // User on load
  useEffect(() => {
    setAvailLocations(adjustLocations());
    if (user) {
      setCurrentLocationCode(parseInt(('' + user.locationId), 10));
    }
  }, [user, adjustLocations]);

  // On form submit.
  const submitForm = useCallback(() => {
    form.validateFields((err, values) => {
      if (!err) {

        // Location validation
        if (isInspector && (currentLocationCode == null || currentLocationCode === -1 || locationObjects == null || locationObjects.length < 1)) {
          setValidationLocationsBox(intl.formatMessage({ id: 'missingLocations' }));
        } else {
          setValidationLocationsBox(null);

          // To keep list current
          let newLocationName = null;
          let newLocationId = null;
          if (isInspector) {
            newLocationId = currentLocationCode;
            if (locationObjects != null) {
              locationObjects.forEach(loc => {
                if (loc.locationCode === newLocationId) {
                  newLocationName = loc.locationDescription;
                }
              });
            }
          }

          // Save Object
          const newUser = user
              ? {
                ...user,
                username: values.username,
                displayName: values.displayName,
                linkedRoles: values.linkedRoles,
                locationId: newLocationId,
                locations: isInspector ? locationObjects : [],
                locationName: newLocationName,
                active: values.status === userStatusNames.ACTIVE,
              }
              : {
                active: false,
                emailVerified: false,
                username: values.username,
                displayName: values.displayName,
                linkedRoles: values.linkedRoles,
                locationId: newLocationId,
                locations: isInspector ? locationObjects : [],
                locationName: newLocationName,
              };

          const submitFunction = isCreating ? onCreate : onUpdate;
          submitFunction(newUser, intl);
        }
      }
    });
  }, [form, user, isCreating, onCreate, onUpdate, intl, isInspector, locationObjects, currentLocationCode]);

  return (
    <Modal
      title={<FormattedMessage id={titleId} />}
      okText={<FormattedMessage id={buttonId} />}
      onOk={submitForm}
      visible={visible}
      onCancel={onCancel}
      confirmLoading={isSubmitting}
      style={{ top: '2px' }}
      {...rest}
    >
      {isSubmitting
          ?
          <div>
            <Skeleton title={false} paragraph={{ width: [50, 200, 200, 100, 100, 50, 200, 50, 100, 100, 100, 100, 100, 50, 200, 200, 200, 200, 200, 200, 70], rows: isInspector ? 21 : 10 }} />
          </div>
          :
          <Form layout="vertical" onSubmit={(e) => {
            e.preventDefault();
            submitForm();
          }}>
            <ViaValidationAlert validationError={validationBox}/>
            <UsernameField form={form} user={user} checkForDuplicates={usernameValidator}/>
            <StatusField form={form} user={user} sessionUsername={sessionUser.username}/>
            <DisplayNameField form={form} user={user}/>
            <RolesFields form={form} user={user} isLastAdmin={isLastAdmin(user ? user.username : null)} isLastCompanyAdmin={isLastCompanyAdmin(user ? user.username : null)}
                         defaultChecked={isAdmin} setIsInspector={setIsInspector} sessionUser={sessionUser}/>
            {isInspector && !userIsAdminConsole(sessionUser)
                ?
                <div>
                  {validationLocationsBox != null ? <Alert message={validationLocationsBox} type="warning"/> : <></>}
                  <LocationsMiniTable form={form} locationList={locationObjects} currentLocCode={currentLocationCode}
                                      setCurrentLocCode={setCurrentLocationCode} loading={isLoading}
                                      onLocationRemove={onLocationRemove}/>
                  <LocationField form={form} user={user} locations={availLocations} loading={isLoading}
                                 addLocationAction={addLocationAction} selectValue={selectValue}/>
                </div>
                : <></>}
          </Form>
      }
    </Modal>
  );
}));

// Validate the prop type parameters.
EditUser.propTypes = {
  visible: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    username: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    emailVerified: PropTypes.bool.isRequired,
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
    locationId: PropTypes.number,
    locations: PropTypes.array,
    active: PropTypes.bool.isRequired,
  }),
  locationObjects: PropTypes.array,
  sessionUser: PropTypes.shape({
    username: PropTypes.string.isRequired,
  }),
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  locations: PropTypes.arrayOf(locationShape).isRequired,
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  isLastAdmin: PropTypes.func.isRequired,
  isLastCompanyAdmin: PropTypes.func.isRequired,
  getLocations: PropTypes.func.isRequired,
  validationLocationsBox: PropTypes.string,
};

EditUser.defaultProps = {
  user: null,
  locationObjects: [],
  isLoading: false,
  validationBox: null,
  locations: [],
  validationLocationsBox: null,
};

const mapStateToProps = (state) => {
  const editState = selectEditState(state);
  const submitRequest = selectCreateUserRequest(state);
  const pageDataRequest = selectUsersRequest(state);
  const locations = selectLocations(state);

  const selectedUser = editState.userId ? selectUser(state, editState.userId) : null;

  const copyLocations = [];
  if (selectedUser != null) {
    selectedUser.locations.forEach(loc => copyLocations.push(loc));
  }

  return {
    visible: editState.isEditing,
    user: selectedUser,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    validationBox: submitRequest ? submitRequest.error : undefined,
    sessionUser: selectSessionUser(state),
    locations: sortAlphabeticallyByAttribute(locations, 'locationDescription', false) || [],
    locationObjects: copyLocations,
  };
};

const mapDispatchToProps = dispatch => ({
  getLocations: () => dispatch(requestLocationForSelect()),
  onCreate: (user, intl) => dispatch(requestAddUser(user, intl)),
  onUpdate: user => dispatch(requestUpdateUser(user)),
  onCancel: () => dispatch(cancelEditUser()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditUser);
